import React, { ReactNode } from "react";
import { BookHalf, Sun, Moon } from "react-bootstrap-icons";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { DocsSidebarEntry, MetricConfig, WidgetTheme } from "../../../types/analytics";
import { getWidgetTheme } from "../../../utils/analytics";
import { getOperationalAnalyticsWidget } from "../../../utils/Requests";
import { getAvailableCustomers } from "../../../config/Customers";
import { DocsSidebar, InteractiveLineChart, OperationalMetricCards, WidgetCard } from "../../../components/analytics";
import type { OperationalMetricCardItem } from "../../../components/analytics/OperationalMetricCards";

type InternalLandingProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type InternalLandingState = {
    isLoading: boolean;
    isOperationalLoading: boolean;
    operationalError: string | null;
    operationalRows: Array<Record<string, any>>;
    operationalSchema: Record<string, any> | null;
    operationalWidgetDescription: string | null;
    isDarkTheme: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    operationalWindowDays: number;
    operationalSelectedCustomer: string;
    isChartLoading: boolean;
    chartError: string | null;
    chartData: Array<Record<string, any>>;
    chartWidgetDescription: string | null;
    userChartMetrics: MetricConfig[];
    playChartMetrics: MetricConfig[];
    metricMode: "users" | "plays";
};

class InternalLanding extends React.Component<InternalLandingProps, InternalLandingState> {
    private spinnerTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly sidebarEntryOrder = ["operational_platform_overview", "customer_weekly_stats"];

    private getSchemaDescription = (schema: Record<string, { description?: string }> | null, keys: string[]): string | undefined => {
        if (!schema) return undefined;
        for (const key of keys) {
            const description = schema[key]?.description;
            if (description) return description;
        }
        return undefined;
    };

    state = {
        isLoading: true,
        isOperationalLoading: true,
        operationalError: null as string | null,
        operationalRows: [] as Array<Record<string, any>>,
        operationalSchema: null as Record<string, any> | null,
        operationalWidgetDescription: null as string | null,
        isDarkTheme: true,
        sidebarOpen: false,
        sidebarEntries: [] as DocsSidebarEntry[],
        operationalWindowDays: 30,
        operationalSelectedCustomer: "all",
        isChartLoading: true,
        chartError: null as string | null,
        chartData: [] as Array<Record<string, any>>,
        chartWidgetDescription: null as string | null,
        userChartMetrics: [] as MetricConfig[],
        playChartMetrics: [] as MetricConfig[],
        metricMode: "users" as "users" | "plays"
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private upsertSidebarEntry = (entry: DocsSidebarEntry): void => {
        this.setState(prevState => ({
            sidebarEntries: [
                ...prevState.sidebarEntries.filter((existingEntry: DocsSidebarEntry) => existingEntry.widgetKey !== entry.widgetKey),
                entry
            ].sort((a, b) => this.sidebarEntryOrder.indexOf(a.widgetKey) - this.sidebarEntryOrder.indexOf(b.widgetKey))
        }));
    };

    private readonly apiFields = {
        customer: "customer",
        dateGenerated: "date_generated",
        usersLast7days: "users_last_7days",
        playsLast7days: "plays_last_7days"
    } as const;

    private readonly seriesPalette = [
        "#1976d2", "#e53935", "#43a047", "#fb8c00", "#8e24aa", "#00acc1", "#6d4c41", "#3949ab"
    ];

    private readonly operationalMetricMeta: Record<string, { label: string; valueFormat?: "number" | "percent" | "durationHours" | "numberCompactMillionsWithTooltip" | "durationHoursCompactMillionsWithTooltip" }> = {
        "active_customers": { label: "Active customers" },
        "dau": { label: "DAU" },
        "wau": { label: "WAU" },
        "mau": { label: "MAU" },
        "stickiness": { label: "Stickiness", valueFormat: "percent" },
        "video_starts": { label: "Video starts", valueFormat: "numberCompactMillionsWithTooltip" },
        "watch_hours": { label: "Watch hours", valueFormat: "durationHoursCompactMillionsWithTooltip" },
        "completion_rate": { label: "Completion rate", valueFormat: "percent" },
        "rec_ctr": { label: "Rec CTR", valueFormat: "percent" },
        "ad_revenue": { label: "Ad revenue" },
        "rpm": { label: "RPM" },
        "error_rate": { label: "Error rate", valueFormat: "percent" }
    };

    public componentDidMount(): void {
        this.spinnerTimer = setTimeout(() => {
            this.setState({ isLoading: false });
        }, 800);

        this.fetchOperationalOverview();
        this.fetchWeeklyStats();
    }

    public componentWillUnmount(): void {
        if (this.spinnerTimer) {
            clearTimeout(this.spinnerTimer);
        }
    }

    private toMetricKey = (customer: string, suffix: "users_last_7days" | "plays_last_7days"): string => {
        const normalized = customer.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
        return `customer_${normalized}_${suffix}`;
    };

    private aggregateByDateAndCustomer = (rows: Array<Record<string, any>>): { chartData: Array<Record<string, any>>; userMetrics: MetricConfig[]; playMetrics: MetricConfig[] } => {
        const byDate: Record<string, Record<string, number | string>> = {};
        const customers = Array.from(new Set(rows.map((row) => String(row[this.apiFields.customer] || "")).filter(Boolean))).sort();

        rows.forEach((row) => {
            const date = row[this.apiFields.dateGenerated];
            const customer = String(row[this.apiFields.customer] || "");
            if (!date || !customer) return;
            if (!byDate[date]) {
                byDate[date] = { [this.apiFields.dateGenerated]: date };
            }

            const usersKey = this.toMetricKey(customer, "users_last_7days");
            const playsKey = this.toMetricKey(customer, "plays_last_7days");
            byDate[date][usersKey] = Number(row[this.apiFields.usersLast7days] || 0);
            byDate[date][playsKey] = Number(row[this.apiFields.playsLast7days] || 0);
        });

        const chartData = Object.keys(byDate)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map((date) => byDate[date]);

        const userMetrics: MetricConfig[] = customers.map((customer, idx) => {
            const base = idx % this.seriesPalette.length;
            return {
                key: this.toMetricKey(customer, "users_last_7days"),
                name: customer,
                color: this.seriesPalette[base]
            };
        });

        const playMetrics: MetricConfig[] = customers.map((customer, idx) => {
            const base = idx % this.seriesPalette.length;
            return {
                key: this.toMetricKey(customer, "plays_last_7days"),
                name: customer,
                color: this.seriesPalette[base]
            };
        });

        return { chartData, userMetrics, playMetrics };
    };

    private fetchWeeklyStats = async (): Promise<void> => {
        this.setState({ isChartLoading: true, chartError: null });

        try {
            const response = await getOperationalAnalyticsWidget("customer_weekly_stats");
            const responseAny = response as any;
            const dataArray = responseAny?.result || (Array.isArray(response) ? response : []);

            if (Array.isArray(dataArray) && dataArray.length > 0) {
                const { chartData, userMetrics, playMetrics } = this.aggregateByDateAndCustomer(dataArray);
                const meta = responseAny?.widget;
                if (meta) {
                    /* eslint-disable camelcase */
                    this.upsertSidebarEntry({
                        widgetKey: "customer_weekly_stats",
                        displayName: meta.display_name,
                        description: meta.description,
                        schema: responseAny?.schema
                    });
                    /* eslint-enable camelcase */
                }
                this.setState({ chartData, userChartMetrics: userMetrics, playChartMetrics: playMetrics, isChartLoading: false, chartError: null });
                return;
            }

            this.setState({
                chartData: [],
                userChartMetrics: [],
                playChartMetrics: [],
                isChartLoading: false,
                chartError: "No weekly customer stats available"
            });
        } catch (_error) {
            this.setState({
                chartData: [],
                userChartMetrics: [],
                playChartMetrics: [],
                isChartLoading: false,
                chartError: "Failed to load customer weekly stats"
            });
        }
    };

    private toIsoDate = (d: Date): string => d.toISOString().split("T")[0];

    private fetchOperationalOverview = async (): Promise<void> => {
        const { operationalWindowDays, operationalSelectedCustomer } = this.state;
        const endDate = new Date();

        const startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - (operationalWindowDays - 1));

        this.setState({ isOperationalLoading: true, operationalError: null });

        try {
            const response = await getOperationalAnalyticsWidget(
                "operational_platform_overview",
                this.toIsoDate(startDate),
                this.toIsoDate(endDate),
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                operationalSelectedCustomer !== "all"
                    ? { customer: operationalSelectedCustomer }
                    : undefined
            );

            const responseAny = response as any;
            const rows = Array.isArray(responseAny?.result) ? responseAny.result : (Array.isArray(response) ? response : []);
            const schema = (responseAny?.schema && typeof responseAny.schema === "object") ? responseAny.schema : null;
            const widgetDescription = responseAny?.widget?.description || null;
            const meta = responseAny?.widget;
            if (meta) {
                /* eslint-disable camelcase */
                this.upsertSidebarEntry({
                    widgetKey: "operational_platform_overview",
                    displayName: meta.display_name,
                    description: meta.description,
                    schema: responseAny?.schema
                });
                /* eslint-enable camelcase */
            }
            this.setState({
                isOperationalLoading: false,
                operationalRows: rows,
                operationalSchema: schema,
                operationalWidgetDescription: widgetDescription,
                operationalSelectedCustomer,
                operationalError: null
            });
        } catch (_error) {
            this.setState({
                isOperationalLoading: false,
                operationalRows: [],
                operationalSchema: null,
                operationalWidgetDescription: null,
                operationalError: "Failed to load operational platform overview"
            });
        }
    };

    private buildOperationalCards = (): OperationalMetricCardItem[] => {
        const { operationalRows, operationalSchema, operationalSelectedCustomer } = this.state;
        const availableCustomerConfigs = getAvailableCustomers(this.props.groups);
        const selectedCustomerConfig = availableCustomerConfigs.find((customer) => customer.slug === operationalSelectedCustomer);
        const hasCustomerColumnData = operationalRows.some((row) => String(row.customer || "").trim().length > 0);
        const filteredRows = operationalSelectedCustomer === "all"
            ? operationalRows
            : hasCustomerColumnData
                ? operationalRows.filter((row) => {
                    const rowCustomer = String(row.customer || "");
                    return rowCustomer === operationalSelectedCustomer || rowCustomer === (selectedCustomerConfig?.name || "");
                })
                : operationalRows;

        return filteredRows
            // Hidden due to no metric data available.
            .filter((row) => !["ad_revenue", "rpm"].includes(String(row.metric || "")))
            .map((row) => {
            const key = String(row.metric || "");
            const meta = this.operationalMetricMeta[key] || { label: key.replace(/_/g, " ") };
            const rawValue = row.value;
            const rawDelta = row.delta_pct;
            const rowDescription = typeof row.description === "string" && row.description.trim().length > 0
                ? row.description
                : undefined;
            return {
                key,
                label: meta.label,
                value: rawValue == null ? null : Number(rawValue),
                changePercent: rawDelta == null ? null : Number(rawDelta),
                valueFormat: meta.valueFormat || "number",
                description: rowDescription || this.getSchemaDescription(operationalSchema, [key, "value"]),
                sparkline: Array.isArray(row.sparkline_30d) ? row.sparkline_30d.map((n: any) => Number(n || 0)) : [],
                sparklineAverage: Array.isArray(row.sparkline_30d_avg) ? row.sparkline_30d_avg.map((n: any) => Number(n || 0)) : []
            };
            });
    };

    public render(): ReactNode {
        const {
            isOperationalLoading,
            operationalError,
            operationalRows,
            operationalWidgetDescription,
            operationalWindowDays,
            operationalSelectedCustomer,
            isDarkTheme,
            isChartLoading,
            chartError,
            chartData,
            sidebarOpen,
            sidebarEntries,
            userChartMetrics,
            playChartMetrics,
            metricMode
        } = this.state;
        const theme: WidgetTheme = getWidgetTheme(isDarkTheme);
        const operationalCards = this.buildOperationalCards();
        const availableCustomers = getAvailableCustomers(this.props.groups)
            .sort((a, b) => a.name.localeCompare(b.name));
        const activeMetrics = metricMode === "users" ? userChartMetrics : playChartMetrics;
        const chartTitle = metricMode === "users"
            ? "Customer Weekly Users (last 6 months)"
            : "Customer Weekly Plays (last 6 months)";

        return (
            <>
                <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Container className="mw-100" style={{ paddingBottom: "40px", position: "relative" }}>
                            <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                                <Button
                                    onClick={this.toggleTheme}
                                    style={{ backgroundColor: "transparent", border: "none", fontSize: "24px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                                    title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                                >
                                    {isDarkTheme ? <Sun /> : <Moon />}
                                </Button>
                                <Button
                                    onClick={this.toggleSidebar}
                                    style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                                    title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                                >
                                    <BookHalf />
                                </Button>
                            </div>
                            <Row>
                                <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                                    <h2 style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: "10px", color: theme.text }}>
                                        Internal - Overview
                                    </h2>
                                </Col>
                            </Row>
                        </Container>
                        <Container className="mw-100" style={{ marginTop: "10px" }}>
                            <WidgetCard
                                title="Operational Platform Overview"
                                description={operationalWidgetDescription || undefined}
                                titleAdornment={(
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={String(operationalWindowDays)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ operationalWindowDays: Number(e.target.value) })}
                                            style={{ width: "120px" }}
                                        >
                                            <option value="7">Last 7 days</option>
                                            <option value="30">Last 30 days</option>
                                            <option value="90">Last 90 days</option>
                                        </Form.Control>
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={operationalSelectedCustomer}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ operationalSelectedCustomer: e.target.value })}
                                            style={{ width: "170px" }}
                                        >
                                            <option value="all">All customers</option>
                                            {availableCustomers.map((customer) => (
                                                <option key={customer.slug} value={customer.slug}>{customer.name}</option>
                                            ))}
                                        </Form.Control>
                                        <Button size="sm" variant="outline-primary" onClick={this.fetchOperationalOverview}>
                                            Apply
                                        </Button>
                                    </div>
                                )}
                                theme={theme}
                                isLoading={isOperationalLoading}
                                error={operationalError}
                                isEmpty={!isOperationalLoading && !operationalError && operationalRows.length === 0}
                                emptyMessage="No operational platform overview data returned"
                            >
                                <OperationalMetricCards
                                    metrics={operationalCards}
                                    theme={theme}
                                    hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                                    emptyMessage="No operational platform overview data returned"
                                    minCardWidth="180px"
                                    maxCardWidth="200px"
                                    gridGap="16px"
                                />
                            </WidgetCard>

                            <WidgetCard
                                title={chartTitle}
                                titleAdornment={(
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <Button
                                            size="sm"
                                            variant={metricMode === "users" ? "primary" : "outline-primary"}
                                            onClick={() => this.setState({ metricMode: "users" })}
                                        >
                                            Users
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant={metricMode === "plays" ? "primary" : "outline-primary"}
                                            onClick={() => this.setState({ metricMode: "plays" })}
                                        >
                                            Plays
                                        </Button>
                                    </div>
                                )}
                                theme={theme}
                                isLoading={isChartLoading}
                                error={chartError}
                                isEmpty={!isChartLoading && !chartError && chartData.length === 0}
                                emptyMessage="No weekly customer stats available"
                            >
                                <InteractiveLineChart
                                    key={metricMode}
                                    title=""
                                    data={chartData}
                                    xAxisDataKey={this.apiFields.dateGenerated}
                                    metrics={activeMetrics}
                                    theme={theme}
                                    height={380}
                                    tooltipLabelFormatter={(value: string) => new Date(value).toLocaleDateString()}
                                    tooltipValueFormatter={(value: number) => Number(value || 0).toLocaleString()}
                                />
                            </WidgetCard>
                        </Container>
                    </div>
                    <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
                </div>
            </>
        );
    }
}

export default InternalLanding;